import { React, variantProvider } from '@/app'
import { CenterWrapper, Text, HTMLRender, Image, View, LearnStore, LearnOption, ModalLessonAnswer, useLessonContext, PlayerFullscreen } from '@/components'
import { TypeGuards } from '@codeleap/common'
import { Page, QuizChoice, QuizQuestion } from '@/types'
import { CSSProperties, ReactNode, useMemo, useState } from 'react'
import { useIsMobile } from '@/utils'
import { AppStatus } from '@/redux'
import { APIClient } from '@/services'
import { globalState } from '@codeleap/store'
import { AnyFunction } from '@codeleap/types'

const quizFeedback = globalState<QuizQuestion['id'] | null>(null)

const toggleFeedback = (id: QuizQuestion['id']) => {
  const currentFeedback = quizFeedback.get()

  if (!currentFeedback) {
    return quizFeedback.set(id)
  }

  if (id === currentFeedback) {
    return quizFeedback.set(0)
  }

  return quizFeedback.set(id)
}

type BodyProps = {
  page: Page
  question: QuizQuestion
  visible?: boolean
  hasChildren?: boolean
  children?: ReactNode
  style?: CSSProperties
}

type LessonViewContentProps = BodyProps & {
  renderOverlay: JSX.Element
}

const Body = (props: BodyProps) => {
  const { page, question, visible = true, hasChildren = false, children, style } = props

  const padding = !!page?.question || hasChildren
  const assignContentStyles = !!page?.question || hasChildren
  const autoWidth = hasChildren
  const text = !page?.question ? page?.text : null

  return (
    <div
      css={[
        styles.innerWrapper,
        { pointerEvents: visible ? 'auto' : 'none' },
        padding && styles.padding,
        assignContentStyles && styles.content,
        autoWidth && styles.autoWidth,
        style,
      ]}
    >
      {TypeGuards.isString(question?.title) ? (
        <Text variants={['h2']} text={question?.title} />
      ) : null}

      {TypeGuards.isString(question?.description) ? (
        <Text variants={['p1', 'marginTop:2']} text={question?.description} />
      ) : null}

      {children}

      {!TypeGuards.isNil(text) ? (
        <HTMLRender html={text} />
      ) : null}
    </div>
  )
}

const Img = (props: Pick<LessonViewContentProps, 'question' | 'page' | 'style'>) => {
  const { page, question, style } = props

  const isMobile = useIsMobile()

  if (!page?.mobile_foreground && !page?.web_foreground) return null

  return (
    <Image
      source={isMobile ? page?.mobile_foreground : page?.web_foreground}
      style={style}
      variants={[(page?.video || page?.text) && 'marginBottom:1', (!!question?.title || !!question?.description) && 'marginTop:1']}
      objectFit='contain'
    />
  )
}

const Quiz = (props: Pick<LessonViewContentProps, 'question' | 'page' | 'style'>) => {
  const { page, question, style } = props

  const selectable = (!!question?.id && !!page?.question)

  const isNeutral = question?.is_neutral

  const [selectedChoice, setSelectedChoice] = LearnStore(state => ([state?.choices?.[question?.id], state?.setChoice]))

  const onSelectQuizChoice = async (_choice: QuizChoice) => {
    setSelectedChoice(question?.id, _choice)
    AppStatus.set('loading')

    if (APIClient.Session.isLoggedIn()) {
      await APIClient.Learn.sendAnswer(question?.id, _choice?.id)
    }

    AppStatus.set('idle')
    toggleFeedback(question?.id)
  }

  const RenderQuizChoice = React.useCallback(({ item }) => {
    const isSelected = item?.id === selectedChoice?.id
    const selectedWrongChoice = !!selectedChoice && !isNeutral && !selectedChoice?.is_correct
    const isCorrect = item?.is_correct

    return (
      <LearnOption
        extraSpacing
        key={'learn-filter-' + item?.id}
        text={item?.label}
        onPress={() => onSelectQuizChoice(item)}
        selected={isSelected || (selectedWrongChoice && isCorrect)}
        isCorrect={isCorrect}
        isNeutral={isNeutral}
      />
    )
  }, [selectedChoice, onSelectQuizChoice])

  if (!page?.question) return null

  return (
    // @ts-ignore
    <View
      css={[
        styles.quizChoiceList,
        { pointerEvents: selectable ? 'auto' : 'none' },
        style,
      ]}
    >
      {question?.choices?.map((item, i) => <RenderQuizChoice item={item} />)}
    </View>
  )
}

const Video = ({ page, style }: Pick<LessonViewContentProps, 'page' | 'style'>) => {
  const { inSwipe } = useLessonContext()

  const [fullscreen, setFullscreen] = useState(false)

  if (!page?.video) return null

  return <>
    {/* @ts-ignore */}
    <View
      css={[styles.videoOverlay, style]}
      onClick={() => {
        if (!inSwipe.current) setFullscreen(true)
      }}
    />

    <PlayerFullscreen
      url={page?.video}
      visible={fullscreen}
      toggle={() => setFullscreen(!fullscreen)}
    />
  </>
}

const QuizFeedback = (props: Pick<LessonViewContentProps, 'question'> & { onNext?: AnyFunction }) => {
  const { question, onNext } = props

  const visible = quizFeedback.use() === question?.id

  const selectedChoice = LearnStore(state => state?.choices?.[question?.id])

  const isNeutral = question?.is_neutral
  const isCorrect = selectedChoice?.is_correct

  const { type, title, text } = useMemo(() => {
    const type = isNeutral ? 'neutral' : (isCorrect ? 'correct' : 'incorrect')
    const title = isCorrect || isNeutral ? 'correct_feedback_title' : 'incorrect_feedback_title'
    const text = isCorrect || isNeutral ? 'correct_feedback' : 'incorrect_feedback'

    return {
      type,
      title: question?.[title],
      text: question?.[text]
    }
  }, [isCorrect, question])

  return (
    <ModalLessonAnswer
      visible={visible}
      toggle={() => toggleFeedback(question?.id)}
      type={type as any}
      title={title}
      text={text}
      onNext={onNext}
    />
  )
}

QuizFeedback.toggle = toggleFeedback

export const LessonViewContent = (props: LessonViewContentProps) => {
  const { renderOverlay, ...rest } = props

  return (
    <CenterWrapper styles={{ 'wrapper': styles.wrapper, 'innerWrapper': styles.centerInner }}>
      <Body {...rest} />

      {renderOverlay}
    </CenterWrapper>
  )
}

LessonViewContent.Body = Body
LessonViewContent.Img = Img
LessonViewContent.Quiz = Quiz
LessonViewContent.QuizFeedback = QuizFeedback
LessonViewContent.Video = Video

const styles = variantProvider.createComponentStyle(theme => ({
  centerInner: {
    ...theme.presets.center,
  },
  innerWrapper: {
    width: `100%`,
    height: 'auto',
    backgroundColor: theme.colors.transparent,
    ...theme.presets.column,
    ...theme.presets.alignStart,
    ...theme.presets.justifyStart,
    position: 'relative',
    marginTop: theme.spacing.value(12),
    marginBottom: theme.spacing.value(6),
    zIndex: 2,
  },
  content: {
    backgroundColor: theme.colors.neutral1,
    // @ts-ignore
    borderRadius: theme.borderRadius.large,
  },
  wrapper: {
    minHeight: '100svh',
    height: 'auto',
    ...theme.presets.row,
    ...theme.presets.center,
    ...theme.presets.absolute,
    right: 0,
    left: 0,
  },
  padding: {
    ...theme.spacing.padding(4),

    [theme.media.down('small')]: {
      ...theme.spacing.padding(2),
    }
  },
  autoWidth: {
    width: 'auto',
  },
  quizChoiceList: {
    height: 'auto',
    ...theme.presets.column,
    ...theme.presets.fullWidth,
    marginTop: theme.spacing.value(1),
  },
  videoOverlay: {
    ...theme.presets.absolute,
    right: 0,
    left: 0,
    top: 0,
  }
}), true)